/* P.Louise — Snowfall Animation
   Port of the Bundui "Snowfall Animation Background" (@bundui/snowfall-animation)
   to a vanilla-JS theme section. A canvas sits behind the section content
   and draws gently drifting snowflakes.

   Behaviour notes:
   - Flake count is capped by the section's area (one flake per 10,000px²),
     as in the original, so small screens stay light.
   - The canvas follows the section's size (ResizeObserver) and is drawn at
     device pixel ratio so flakes stay crisp.
   - The loop pauses while the section is off screen or the tab is hidden.
   - Reduced-motion users get a single still frame of snow.
   - The section may be placed more than once; each copy runs on its own. */

(function () {
  'use strict';

  if (window.__plSnowfall) return;
  window.__plSnowfall = true;

  var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var instances = [];

  function num(root, name, fallback) {
    var v = parseFloat(root.getAttribute(name));
    return isNaN(v) ? fallback : v;
  }

  /* Weighted sway: most flakes drift a little, a few drift a lot. */
  function naturalAmplitude() {
    var r = Math.random();
    if (r < 0.3) return 0.1 + Math.random() * 0.3;
    if (r < 0.9) return 0.5 + Math.random() * 1.0;
    return 1.6 + Math.random() * 1.4;
  }

  function hexToRgb(hex) {
    var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return '255, 255, 255';
    var h = m[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    return ((n >> 16) & 255) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255);
  }

  function init(root) {
    if (!root || root.getAttribute('data-snowfall-init') === 'true') return;
    var canvas = root.querySelector('[data-snowfall-canvas]');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    root.setAttribute('data-snowfall-init', 'true');

    var opts = {
      count:     num(root, 'data-count', 100),
      minRadius: num(root, 'data-min-radius', 1),
      maxRadius: num(root, 'data-max-radius', 4),
      minSpeed:  num(root, 'data-min-speed', 0.5),
      maxSpeed:  num(root, 'data-max-speed', 2),
      wind:      num(root, 'data-wind', 0.5),
      rgb:       hexToRgb(root.getAttribute('data-color'))
    };
    if (opts.maxRadius < opts.minRadius) opts.maxRadius = opts.minRadius;
    if (opts.maxSpeed < opts.minSpeed) opts.maxSpeed = opts.minSpeed;

    var width = 0;
    var height = 0;
    var flakes = [];
    var frame = 0;
    var inView = true;
    var running = false;

    function makeFlake(y) {
      return {
        x: Math.random() * width,
        y: y,
        radius: opts.minRadius + Math.random() * (opts.maxRadius - opts.minRadius),
        speed: opts.minSpeed + Math.random() * (opts.maxSpeed - opts.minSpeed),
        opacity: 0.3 + Math.random() * 0.7,
        wind: (Math.random() - 0.5) * opts.wind * 0.5,
        amplitude: naturalAmplitude(),
        frequency: 0.001 + Math.random() * 0.008,
        angle: Math.random() * Math.PI * 2
      };
    }

    function seed() {
      var total = Math.min(opts.count, Math.floor((width * height) / 10000));
      flakes = [];
      for (var i = 0; i < total; i++) flakes.push(makeFlake(Math.random() * height));
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);
      for (var i = 0; i < flakes.length; i++) {
        var f = flakes[i];
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(' + opts.rgb + ', ' + f.opacity + ')';
        ctx.fill();
      }
    }

    function step() {
      for (var i = 0; i < flakes.length; i++) {
        var f = flakes[i];
        f.y += f.speed;
        f.angle += f.frequency;
        f.x += f.wind + Math.sin(f.angle) * f.amplitude;

        if (f.y > height + f.radius) {
          f.y = -f.radius;
          f.x = Math.random() * width;
          f.amplitude = naturalAmplitude();
          f.frequency = 0.001 + Math.random() * 0.008;
        }
        if (f.x > width) f.x = 0;
        else if (f.x < 0) f.x = width;
      }
    }

    function tick() {
      step();
      draw();
      frame = requestAnimationFrame(tick);
    }

    function start() {
      if (running || REDUCED_MOTION || !inView || document.hidden || !flakes.length) return;
      running = true;
      frame = requestAnimationFrame(tick);
    }

    function stop() {
      running = false;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }

    function resize() {
      var rect = root.getBoundingClientRect();
      var w = Math.round(rect.width);
      var h = Math.round(rect.height);
      if (w === width && h === height) return;
      width = w;
      height = h;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
      draw();
      if (flakes.length) start(); else stop();
    }

    var ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(resize);
      ro.observe(root);
    } else {
      window.addEventListener('resize', resize);
    }

    var io = null;
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView) start(); else stop();
      });
      io.observe(root);
    }

    function onVisibility() {
      if (document.hidden) stop(); else start();
    }
    document.addEventListener('visibilitychange', onVisibility);

    resize();

    instances.push({
      root: root,
      destroy: function () {
        stop();
        if (ro) ro.disconnect(); else window.removeEventListener('resize', resize);
        if (io) io.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        root.removeAttribute('data-snowfall-init');
      }
    });
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-snowfall]');
    Array.prototype.forEach.call(nodes, init);
  }

  function destroyIn(scope) {
    instances = instances.filter(function (inst) {
      if (scope.contains(inst.root)) {
        inst.destroy();
        return false;
      }
      return true;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target) destroyIn(e.target);
  });
})();